/**
 * <SharesTableHeader> — column-header row for the Sent + Received share
 * lists. Must use the same `grid` template + `gap-4` as the row component
 * so headers align with the data columns.
 *
 * Action columns aren't labeled (the buttons speak for themselves), but
 * the action column still needs a grid slot so the trailing columns align.
 */
import clsx from 'clsx';

export interface SharesTableHeaderColumn {
  label: string;
  /**
   * Optional explicit alignment. Defaults to `left`. Use `right` for
   * numeric columns (`tabular-nums`) so the header sits over the digits.
   */
  align?: 'left' | 'right';
}

export interface SharesTableHeaderProps {
  /** Tailwind grid-template-columns class — MUST match the row component. */
  gridClass: string;
  /**
   * Column labels in render order, including empty-string slots for
   * columns that don't carry a header (e.g. trailing actions).
   */
  columns: SharesTableHeaderColumn[];
  /** Optional testid for the header row. */
  testid?: string;
}

export function SharesTableHeader({ gridClass, columns, testid }: SharesTableHeaderProps) {
  return (
    <div
      data-testid={testid}
      role="row"
      className={clsx(
        gridClass,
        'gap-4 px-4 pb-2 border-b border-panel-border/60',
        'text-micro font-mono uppercase tracking-[0.5px] text-text-muted',
      )}
    >
      {columns.map((col, idx) => (
        <span
          key={`${idx}-${col.label}`}
          role="columnheader"
          className={clsx(
            'truncate',
            col.align === 'right' ? 'text-right' : 'text-left',
          )}
        >
          {col.label}
        </span>
      ))}
    </div>
  );
}
